/**
 * The full new-thread composer. It shares message intake and rendering with an
 * active thread while keeping pre-thread execution choices on this route.
 */
import { useRef, useState } from "react";
import { useCommandPaletteStore } from "../../commandPaletteStore";
import {
  usePrimaryEnvironmentId,
  usePrimaryEnvironmentIsWorkspaceProxy,
} from "../../environments/primary";
import { useDraftSubmit } from "./useDraftSubmit";
import { useDraftExecutionTarget } from "./useDraftExecutionTarget";
import { useDraftProjectChoice } from "./useDraftProjectChoice";
import { useHandleNewThread } from "../../hooks/useHandleNewThread";
import { useSettings } from "../../hooks/useSettings";
import { useTheme } from "../../hooks/useTheme";
import { useServerConfig } from "../../rpc/serverState";
import { useStore } from "../../store";
import { useUiStateStore } from "../../uiStateStore";
import { type ProviderCredentialMode } from "../../cmmd/forgeProviderAccess";
import {
  buildHeadingSegments,
  resolveNewThreadProviderCredentialMode,
  resolveWorktreeMode,
  type WorktreeMode,
} from "./NewThreadLanding.logic";
import { buildComposerPlaceholder } from "../chat/composerPlaceholder";
import { resolveNewThreadPickerProviderProps } from "./draftProviderAvailability";
import { NewThreadComposer, type NewThreadComposerProps } from "./NewThreadComposer";
import { useIsCloudControlSurface } from "./useIsCloudControlSurface";
import { useDraftTurnModes } from "./useDraftTurnModes";
import { NewThreadExpandedImageDialog } from "./NewThreadExpandedImageDialog";
import { useNewThreadComposerImages } from "./useNewThreadComposerImages";
import type { NewThreadLandingSurface } from "./newThreadLandingSurface";
import { useLandingComposerDraftState } from "./useLandingComposerDraftState";

function resolveNewThreadComposerError(
  imageError: string | null,
  submissionError: string | null,
): string | null {
  return imageError ?? submissionError;
}

/**
 * The CMMD refusal code that goes with the message above, or null. An image
 * error wins the message slot, and the code only belongs to the submission's
 * own refusal, so it is dropped whenever the image error is what shows.
 */
function resolveNewThreadComposerErrorCode(
  imageError: string | null,
  submissionErrorCode: string | null,
): string | null {
  return imageError === null ? submissionErrorCode : null;
}

export function NewThreadComposerLanding({
  inventoryState = "composer",
}: {
  readonly inventoryState?: NewThreadLandingSurface;
}) {
  const landingComposer = useLandingComposerDraftState();
  const { landingDraft, keybindings, stash, stashShortcutLabel } = landingComposer;
  const {
    activeEnvironmentId,
    environmentStateById,
    projectOrder,
    lastActiveProject,
    settings,
    resolvedTheme,
    primaryEnvironmentId,
    primaryServerConfig,
    primaryIsWorkspaceProxy,
    handleNewThread,
    openAddProjectSources,
    openAddCloudProjectSources,
  } = useDraftLandingEnvironment();
  const isCloudControlSurface = useIsCloudControlSurface();
  const {
    selectedExecutionId,
    handleSelectProject,
    handleSelectExecution,
    handleAddProject,
    handleSelectNoProject,
    noWorkspaceSelected,
    desktopRuntimeCatalog,
    executionOptions,
    filteredProjectSummaries,
    activeProject,
    activeProjectPresentation,
    activeCloudCatalogRow,
    activeProjectDefaultBranch,
    branch,
    branchUserSelected,
    knownBranches,
    selectBranch,
  } = useDraftProjectChoice({
    activeEnvironmentId,
    environmentStateById,
    isCloudControlSurface,
    projectOrder,
    lastActiveProject,
    openAddProjectSources,
    openAddCloudProjectSources,
  });

  const providerCredentialMode: ProviderCredentialMode =
    resolveNewThreadProviderCredentialMode(selectedExecutionId);
  const { editorRef: composerEditorRef, prompt, composerCursor, onPromptChange } = landingComposer;
  const [worktreeMode, setWorktreeMode] = useState<WorktreeMode>(
    resolveWorktreeMode(settings.defaultThreadEnvMode),
  );
  const { interactionMode, runtimeMode, toggleInteractionMode, setRuntimeMode } =
    useDraftTurnModes();
  const submittingRef = useRef(false);
  const composerImages = useNewThreadComposerImages({
    isDisabled: () => submittingRef.current,
    onAfterDrop: () => composerEditorRef.current?.focusAt(prompt.length),
    images: landingDraft?.images ?? [],
    files: landingDraft?.files ?? [],
    onImagesChange: landingComposer.onImagesChange,
    onFilesChange: landingComposer.onFilesChange,
  });
  const activeProjectSummary = activeProjectPresentation.summary;
  const {
    providerAccess,
    activeDesktopInventoryProject,
    providerInventory,
    providerAvailability,
    orgProviderPolicy,
    selectedModelSelection,
    resolvedSelectedProvider,
    selectedModel,
    modelOptionsByProvider,
    selectProviderModel,
    selectProviderAccount,
  } = useDraftExecutionTarget({
    activeProject,
    activeProjectPresentation,
    activeCloudCatalogRow,
    desktopRuntimeCatalog,
    primaryEnvironmentId,
    primaryServerConfig,
    providerCredentialMode,
  });
  const {
    scratchWorkspaceSupported,
    hasSendableContent,
    modelTriggerFallbackLabel,
    submitting,
    submitError,
    submitErrorCode,
    submitFailureResidual,
    localNotice,
    sendingPhase,
    submit: handleSubmit,
    onCommandKey,
  } = useDraftSubmit({
    noWorkspaceSelected,
    primaryIsWorkspaceProxy,
    activeProject,
    activeCloudCatalogRow,
    activeDesktopInventoryProject,
    selectedExecutionId,
    primaryEnvironmentId,
    primaryServerConfig,
    selectedModelSelection,
    credentialMode: providerCredentialMode,
    branch,
    prompt,
    images: composerImages.images,
    files: composerImages.files,
    terminalContexts: landingDraft?.terminalContexts ?? [],
    onImagesTransferStarted: composerImages.reserveImagesForTransfer,
    onImagesTransferCancelled: composerImages.cancelImageTransfer,
    onImagesTransferred: composerImages.releaseTransferredImages,
    onSubmissionStarted: landingComposer.onSubmissionStarted,
    onSubmissionHydrated: landingComposer.onSubmissionHydrated,
    interactionMode,
    runtimeMode,
    handleNewThread,
    worktreeMode,
    branchUserSelected,
    projectDefaultBranch: activeProjectDefaultBranch,
    providerAccess,
    inventoryKnown: providerInventory.pickerProviderProps.providers !== undefined,
  });
  submittingRef.current = submitting;
  const composerError = resolveNewThreadComposerError(composerImages.error, submitError);
  const composerErrorCode = resolveNewThreadComposerErrorCode(
    composerImages.error,
    submitErrorCode,
  );
  const providerModelControl = buildProviderModelControl({
    providerAccess,
    providerAvailability,
    providerInventory,
    orgProviderPolicy,
    selectedModelSelection,
    resolvedSelectedProvider,
    selectedModel,
    modelOptionsByProvider,
    selectProviderModel,
    selectProviderAccount,
    projectSelected: activeProjectPresentation.key !== null,
    modelTriggerFallbackLabel,
  });
  return (
    <>
      <NewThreadComposer
        inventoryState={inventoryState}
        headingSegments={buildHeadingSegments(activeProjectPresentation.name)}
        resolvedTheme={resolvedTheme}
        editorRef={composerEditorRef}
        prompt={prompt}
        cursor={composerCursor}
        placeholder={buildComposerPlaceholder(activeProjectPresentation.name)}
        submitting={submitting}
        sendingLabel={sendingPhase}
        hasSendableContent={hasSendableContent}
        images={composerImages.images}
        files={composerImages.files}
        terminalContexts={landingDraft?.terminalContexts ?? []}
        keybindings={keybindings}
        imageAttachments={composerImages.controller}
        onRemoveImage={composerImages.removeImage}
        onRemoveFile={composerImages.removeFile}
        onExpandImage={composerImages.onExpandImage}
        context={{
          execution: {
            options: executionOptions,
            selectedId: selectedExecutionId,
            onSelect: handleSelectExecution,
          },
          project: {
            projects: filteredProjectSummaries,
            activeProject: activeProjectSummary,
            onSelect: handleSelectProject,
            onAddProject: handleAddProject,
            onSelectNoProject: handleSelectNoProject,
            allowNoProject: scratchWorkspaceSupported,
          },
          worktree: { mode: worktreeMode, onSelectMode: setWorktreeMode },
          branch: { branch, onSelectBranch: selectBranch, knownBranches },
        }}
        controls={{
          interactionMode,
          runtimeMode,
          providerModel: providerModelControl,
          onToggleInteractionMode: toggleInteractionMode,
          onRuntimeModeChange: setRuntimeMode,
        }}
        feedback={{
          cloudCatalogActive: activeCloudCatalogRow !== null,
          error: composerError,
          errorCode: composerErrorCode,
          failureResidual: submitFailureResidual,
          localNotice,
          sendingPhase,
          onRetry: handleSubmit,
        }}
        onPromptChange={onPromptChange}
        onCommandKeyDown={onCommandKey}
        onSubmit={handleSubmit}
        onRemoveTerminalContext={landingComposer.onRemoveTerminalContext}
        stash={stash.controller}
        stashShortcutLabel={stashShortcutLabel}
        onStashNotice={landingComposer.onStashNotice}
      />
      <NewThreadExpandedImageDialog
        preview={composerImages.expandedImage}
        onClose={composerImages.closeExpandedImage}
      />
    </>
  );
}

function buildProviderModelControl(input: {
  readonly providerAccess: ReturnType<typeof useDraftExecutionTarget>["providerAccess"];
  readonly providerAvailability: ReturnType<typeof useDraftExecutionTarget>["providerAvailability"];
  readonly providerInventory: ReturnType<typeof useDraftExecutionTarget>["providerInventory"];
  readonly orgProviderPolicy: ReturnType<typeof useDraftExecutionTarget>["orgProviderPolicy"];
  readonly selectedModelSelection: ReturnType<
    typeof useDraftExecutionTarget
  >["selectedModelSelection"];
  readonly resolvedSelectedProvider: ReturnType<
    typeof useDraftExecutionTarget
  >["resolvedSelectedProvider"];
  readonly selectedModel: ReturnType<typeof useDraftExecutionTarget>["selectedModel"];
  readonly modelOptionsByProvider: ReturnType<
    typeof useDraftExecutionTarget
  >["modelOptionsByProvider"];
  readonly selectProviderModel: ReturnType<typeof useDraftExecutionTarget>["selectProviderModel"];
  readonly selectProviderAccount: ReturnType<
    typeof useDraftExecutionTarget
  >["selectProviderAccount"];
  readonly projectSelected: boolean;
  readonly modelTriggerFallbackLabel: string | undefined;
}): NewThreadComposerProps["controls"]["providerModel"] {
  const inventoryKnown = input.providerInventory.pickerProviderProps.providers !== undefined;
  const selectedProviderAccountId = resolveSelectedProviderAccountId(input.selectedModelSelection);
  return {
    compact: false,
    provider: input.resolvedSelectedProvider,
    model: input.selectedModel,
    lockedProvider: null,
    ...resolveNewThreadPickerProviderProps({
      inventoryKnown,
      providers: input.providerInventory.providerStatuses,
    }),
    modelOptionsByProvider: input.modelOptionsByProvider,
    providerAvailability: input.providerAvailability,
    orgProviderPolicy: input.orgProviderPolicy,
    lastKnownInventoryPublishedAt: input.providerInventory.lastKnownInventoryPublishedAt,
    providerAccounts:
      input.providerAccess.status === "ready" ? (input.providerAccess.providerAccounts ?? []) : [],
    ...(selectedProviderAccountId ? { selectedProviderAccountId } : {}),
    onProviderAccountChange: input.selectProviderAccount,
    projectSelected: input.projectSelected,
    unresolvedLabel: input.modelTriggerFallbackLabel,
    onProviderModelChange: input.selectProviderModel,
  };
}

function resolveSelectedProviderAccountId(
  selection: ReturnType<typeof useDraftExecutionTarget>["selectedModelSelection"],
): string | undefined {
  if (selection && "providerAccountId" in selection && selection.providerAccountId)
    return selection.providerAccountId;
  return undefined;
}

/**
 * The ambient facts a draft is composed against.
 *
 * Twelve reads from six different stores, none of which the landing page owns
 * and all of which it needs before it can decide anything: which environment is
 * active, what the user's settings say, which environment is primary and
 * whether that primary is a proxy rather than a real execution plane.
 *
 * Gathered here so the component states what it depends on once, rather than
 * interleaving store reads with the decisions that consume them.
 */
function useDraftLandingEnvironment() {
  const activeEnvironmentId = useStore((store) => store.activeEnvironmentId);
  const environmentStateById = useStore((store) => store.environmentStateById);
  const projectOrder = useUiStateStore((store) => store.projectOrder);
  const lastActiveProject = useUiStateStore((store) => store.lastActiveProject);
  const settings = useSettings();
  const { resolvedTheme } = useTheme();
  const primaryEnvironmentId = usePrimaryEnvironmentId();
  const primaryServerConfig = useServerConfig();
  const primaryIsWorkspaceProxy = usePrimaryEnvironmentIsWorkspaceProxy();
  const { handleNewThread } = useHandleNewThread();
  const openAddProjectSources = useCommandPaletteStore((store) => store.openAddProjectSources);
  const openAddCloudProjectSources = useCommandPaletteStore(
    (store) => store.openAddCloudProjectSources,
  );

  return {
    activeEnvironmentId,
    environmentStateById,
    projectOrder,
    lastActiveProject,
    settings,
    resolvedTheme,
    primaryEnvironmentId,
    primaryServerConfig,
    primaryIsWorkspaceProxy,
    handleNewThread,
    openAddProjectSources,
    openAddCloudProjectSources,
  };
}
